/**
 * Prefetch lazy route chunks on sidebar hover / pointer-down.
 * Uses the same dynamic imports as MainRoute — does not change routes or pages.
 */

const prefetched = new Set();

const ROUTE_LOADERS = [
  // Core
  ["modules", () => import("@/pages/ModulesDashboard")],
  ["dashboard", () => import("@/pages/DashboardHome")],
  ["forwarder-dashboard", () => import("@/pages/ForwarderDashboard")],
  ["purchase-orders", () => import("@/pages/PurchaseOrder")],
  ["sales-orders", () => import("@/pages/SalesOrder")],
  ["load-plan", () => import("@/pages/LoadPlan")],
  ["ShipmentList", () => import("@/pages/Shipment")],
  ["mailbox", () => import("@/pages/Mailbox")],
  ["email-summary", () => import("@/pages/EmailSummaryDashboard")],
  ["vision-board", () => import("@/pages/VisionBoard")],
  ["notifications", () => import("@/pages/NotificationCenterPage")],
  ["address-book", () => import("@/pages/AddressBook")],
  ["wms", () => import("@/pages/WMS")],
  ["composer", () => import("@/pages/Composer")],
  ["api-connect", () => import("@/pages/ApiConnect")],
  ["ratematrix", () => import("@/pages/RateMatrix")],
  ["ocean-control-tower", () => import("@/pages/OceanControlTower")],
  ["ocean/schedule", () => import("@/pages/OceanSchedule")],
  ["ocean/tracking", () => import("@/pages/Ocean/OceanContainerTracking")],

  // Export booking / quotes
  ["booking/cancel", () => import("@/pages/Shipment/CancelShipment")],
  ["booking/new", () => import("@/pages/ExportBookingFormPage")],
  ["booking/edit", () => import("@/pages/ExportBookingFormPage")],
  ["booking", () => import("@/pages/Shipment")],
  ["booking-request-list", () => import("@/pages/BookingRequest/BookingRequestList")],
  ["booking-request", () => import("@/pages/BookingRequest/NewBookingRequest")],
  ["rate-quote/new", () => import("@/pages/RateQuote/NewRateQuote")],
  ["rate-quote/edit", () => import("@/pages/RateQuote/NewRateQuote")],
  ["cancel-rate-quote", () => import("@/pages/CancelRateQuote")],
  ["rate-quote", () => import("@/pages/RateQuote")],
  ["rate-agent", () => import("@/pages/RateIntelligenceTabs")],
  ["ratesagent", () => import("@/pages/RatesAgentTabs")],
  ["quotes-bookings", () => import("@/pages/QuotesBookings")],
  ["customs", () => import("@/pages/CustomsFiling")],
  ["containers", () => import("@/pages/ContainerTrackingPage")],
  ["financials", () => import("@/pages/Financials")],

  // Documents
  ["hbl", () => import("@/pages/HBL")],
  ["mbl", () => import("@/pages/MBL")],
  ["status-audit", () => import("@/pages/StatusAudit")],
  ["dock-receipt", () => import("@/pages/DockReceipt/DockReceipt")],
  ["job-costing", () => import("@/pages/JobCosting")],

  // Masters
  ["maintain-vessel", () => import("@/pages/Vessel/MaintainVessel")],
  ["Commodity", () => import("@/pages/Commodity")],
  ["CountryClause", () => import("@/pages/CountryClause")],
  ["Clause", () => import("@/pages/Clause")],
  ["Users", () => import("@/pages/Users")],
  ["ChargesCode", () => import("@/pages/ChargesCode")],
  ["port-place", () => import("@/pages/PortPlace/PortPlace")],
  ["maintain-locations", () => import("@/pages/MaintainLocations/MaintainLocations")],
  ["office-types", () => import("@/pages/OfficeTypes/OfficeTypes")],
  ["terminal-mapping", () => import("@/pages/TerminalMapping/TerminalMapping")],
  ["terminal-mapping/new", () => import("@/pages/TerminalMapping/TerminalMappingForm")],
  ["terminal-mapping/edit", () => import("@/pages/TerminalMapping/TerminalMappingForm")],

  // Ocean import
  ["import-rate-quote/new", () => import("@/pages/OceanImport/ImportRateQuoteForm")],
  ["import-rate-quote/edit", () => import("@/pages/OceanImport/ImportRateQuoteForm")],
  ["import-cancel-rate-quote", () => import("@/pages/OceanImport/ImportCancelRateQuoteList")],
  ["import-rate-quote", () => import("@/pages/OceanImport/ImportRateQuoteList")],
  ["import-booking/cancel", () => import("@/pages/OceanImport/ImportBooking/CancelImportShipment")],
  ["import-booking/new", () => import("@/pages/OceanImport/ImportBooking/ImportBookingForm")],
  ["import-booking/edit", () => import("@/pages/OceanImport/ImportBooking/ImportBookingForm")],
  ["import-booking", () => import("@/pages/OceanImport/ImportBooking/ImportBookingList")],
  ["import-cancel-booking", () => import("@/pages/OceanImport/ImportCancelBookingRequest")],
  ["release-order", () => import("@/pages/OceanImport/ImportBooking/ReleaseOrderList")],
  ["delivery-order", () => import("@/pages/OceanImport/ImportBooking/DeliveryOrderForm")],

  // Accounting
  ["accounting/chart-of-accounts", () => import("@/pages/Accounting/ChartOfAccounts")],
  ["accounting/charge-codes", () => import("@/pages/Accounting/ChargeCodes")],
  ["accounting/invoices", () => import("@/pages/Accounting/Invoice/CustomerInvoices")],
  ["accounting/bills", () => import("@/pages/Accounting/VendorBills")],
  ["accounting/journals", () => import("@/pages/Accounting/JournalEntries")],
  ["accounting/payments", () => import("@/pages/Accounting/Payments")],
  ["accounting/reports", () => import("@/pages/Accounting/Reports")],
  ["accounting", () => import("@/pages/Accounting/ChartOfAccounts")],

  // Reports hubs
  ["ocean/export/reports", () => import("@/pages/reports/ocean/reports/FclReportsHome")],
  ["ocean/import/reports", () => import("@/pages/reports/ocean/reports/FclReportsHome")],
  ["quotation-report", () => import("@/pages/QuotationReport")],
  ["booking-report", () => import("@/pages/BookingReport")],
  ["accounting-report", () => import("@/pages/AccountingReport")],
  ["import-quotation-report", () => import("@/pages/ImportQuotationReport")],
  ["import-booking-report", () => import("@/pages/ImportBookingReport")],

  // AI
  ["ai-agent-setup", () => import("@/pages/AIAgentSetup")],
  ["ai-agent-dashboard", () => import("@/pages/AIAgentDashboard")],
  ["ai-agent-reports", () => import("@/pages/AIAgentReports")],
  ["rfq", () => import("@/pages/RFQ")],
  ["login-history", () => import("@/pages/LoginHistory")],
];

// Longest prefix first so /booking/cancel beats /booking
const SORTED_LOADERS = [...ROUTE_LOADERS].sort((a, b) => b[0].length - a[0].length);

const normalizePath = (path = "") =>
  String(path)
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

/**
 * Prefetch the JS chunk for a route path (e.g. "/booking" or "booking").
 * Safe to call repeatedly — each path is only fetched once.
 */
export const prefetchRoute = (path) => {
  const normalized = normalizePath(path);
  if (!normalized || normalized.includes("://")) return;

  for (const [prefix, loader] of SORTED_LOADERS) {
    const matches =
      normalized === prefix || normalized.startsWith(`${prefix}/`);
    if (!matches) continue;

    if (prefetched.has(prefix)) return;
    prefetched.add(prefix);

    try {
      const result = loader();
      if (result && typeof result.catch === "function") {
        result.catch(() => {
          prefetched.delete(prefix);
        });
      }
    } catch {
      prefetched.delete(prefix);
    }
    return;
  }
};

export default prefetchRoute;
